// Dialog "Segna documento": marca una proposta come fatturata o da_inviare.
// Riusato anche per "Segna inviata" (da_inviare -> fatturata): il caller
// passa forceStatus="fatturata" per pre-selezionare il radio, doc_type/
// numero/data restano precompilati dalla proposta (gia' valorizzati dal
// primo mark). Stesso pattern modale di CallLogDialog (overlay, busy/error
// gestiti qui dentro, Annulla/Salva).
import { useEffect, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { todayLocalISO } from "@/shared/format";
import { markProposal } from "../api";
import { DOC_TYPES_BY_KIND, type DocType, type Proposal } from "../types";

// SF/ST sono corrispettivi (nessun invio SdI) -> fatturata subito; il resto
// resta "da inviare" finche' Davide non fa F11 in Eureka (regola GSheet B,
// 2026-07-08).
const NO_SDI: ReadonlySet<string> = new Set(["SF", "ST"]);

function defaultDocType(proposal: Proposal): DocType {
  const allowed = DOC_TYPES_BY_KIND[proposal.kind];
  return proposal.doc_type && allowed.includes(proposal.doc_type)
    ? proposal.doc_type
    : (allowed[0] ?? "SF");
}

export function MarkDialog({
  proposal,
  onDone,
  onClose,
  forceStatus,
}: {
  proposal: Proposal;
  onDone: () => void;
  onClose: () => void;
  /** "Segna inviata": apre gia' su "fatturata" invece del default per sigla. */
  forceStatus?: "fatturata";
}) {
  const docTypes = DOC_TYPES_BY_KIND[proposal.kind];
  const [docType, setDocType] = useState<DocType>(defaultDocType(proposal));
  const [num, setNum] = useState(proposal.document_number ?? "");
  const [docDate, setDocDate] = useState(proposal.document_date ?? todayLocalISO());
  const [status, setStatus] = useState<"fatturata" | "da_inviare">(
    forceStatus ?? (NO_SDI.has(docType) ? "fatturata" : "da_inviare"),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ESC per annullare (allineato a CallLogDialog). Ignorato durante l'invio.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const submit = async () => {
    if (!num.trim()) {
      setError("Il numero documento è obbligatorio (letto da Eureka).");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await markProposal(proposal.id, {
        doc_type: docType,
        document_number: num.trim(),
        document_date: docDate,
        status,
      });
      onDone();
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Segna documento"
      data-no-open
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="w-full max-w-sm rounded-lg border border-line bg-card p-5 shadow-popover">
        <h3 className="font-display text-[16px] font-bold text-ink">
          Segna documento — {proposal.customer_name}
        </h3>

        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-2">
            Sigla
            <select
              value={docType}
              disabled={busy}
              onChange={(e) => {
                const dt = e.target.value as DocType;
                setDocType(dt);
                // forceStatus ("Segna inviata") resta fisso: cambiare sigla non
                // deve far tornare "da_inviare" un documento gia' inviato.
                setStatus(forceStatus ?? (NO_SDI.has(dt) ? "fatturata" : "da_inviare"));
              }}
              className="h-[44px] rounded-md border border-line bg-card px-3 text-[13px] font-normal text-ink outline-none focus:border-accent disabled:opacity-50"
            >
              {docTypes.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-2">
            Numero (da Eureka)
            <input
              value={num}
              disabled={busy}
              onChange={(e) => setNum(e.target.value)}
              className="h-[44px] rounded-md border border-line bg-card px-3 text-[13px] font-normal text-ink outline-none focus:border-accent disabled:opacity-50"
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-2">
            Data documento
            <input
              type="date"
              value={docDate}
              disabled={busy}
              onChange={(e) => setDocDate(e.target.value)}
              className="h-[44px] rounded-md border border-line bg-card px-3 text-[13px] font-normal text-ink outline-none focus:border-accent disabled:opacity-50"
            />
          </label>
          <fieldset className="text-[12px] text-ink-2">
            <label className="mr-3 inline-flex items-center gap-1">
              <input
                type="radio"
                checked={status === "fatturata"}
                disabled={busy}
                onChange={() => setStatus("fatturata")}
              />
              Fatturata
            </label>
            <label className="inline-flex items-center gap-1">
              <input
                type="radio"
                checked={status === "da_inviare"}
                disabled={busy}
                onChange={() => setStatus("da_inviare")}
              />
              Da inviare (manca F11)
            </label>
            <p className="mt-1.5 text-[11px] text-ink-2">
              SF/ST = fatturata subito · FA/OSS/RC/RCO = da inviare finché non fai F11 in Eureka.
            </p>
            <p className="mt-1.5 text-[11px] text-ink-2">
              OM controlla in Eureka che il documento esista: un numero che Eureka non ha non viene
              segnato.
            </p>
          </fieldset>
        </div>

        {error && (
          <p role="alert" className="mt-3 text-[12px] text-red">
            {error}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-line bg-card px-3 py-2 text-[12px] font-medium text-ink-2 hover:border-ink-4 disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void submit()}
            className="rounded-md border border-accent bg-accent-2 px-3 py-2 text-[12px] font-medium text-white disabled:opacity-50"
          >
            {busy ? "Salvo…" : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}
